import React from 'react'
import { Button } from 'core'

import css from './style.scss'

// A direct, synchronous user click is never blocked by popup blockers, so
// the already-registered Join/Go-to-Event buttons can safely window.open.
const openInNewTab = url => window.open(url, '_blank', 'noopener,noreferrer')

const RegisterButton = props => {
  const {
    isRegistered,
    isLoading,
    register,
    unregister,
    isExternal,
    externalUrl,
    zoomEnabled,
    windowOpen,
    joinUrl
  } = props

  const commonProps = {
    size: 'primary',
    noMargin: true,
    isLoading
  }

  // Register-and-Join must open the tab synchronously in the click handler —
  // browsers block window.open() once the promise below has resolved and
  // the user-gesture token is gone.
  const handleRegisterAndJoin = () => {
    const win = window.open('', '_blank')
    register().then(data => {
      const url = data && (data.zoom_join_url || data.zoom_general_join_url)
      if (!url) {
        if (win) win.close()
        return
      }
      if (win) {
        win.location.href = url
      } else {
        window.location.href = url
      }
    })
  }

  // Unregistering stays available at every stage, including once the join
  // window is open — plans change, and there's no reason to trap someone
  // in a registration they no longer want.
  // When it sits beside a Join/Go-to-Event button it's an outline button so
  // it's visually quieter than the primary action.
  const renderUnregister = outline => (
    <Button
      {...commonProps}
      className={outline ? css.unregisterOutline : ''}
      outline={outline}
      disabled={isLoading}
      onClick={unregister} // eslint-disable-line react/jsx-handler-names
      color='green'
      success={!outline}
    >
      Unregister
    </Button>
  )

  if (isRegistered && zoomEnabled) {
    if (windowOpen && joinUrl) {
      return (
        <span className={css.actions}>
          <Button {...commonProps} onClick={() => openInNewTab(joinUrl)} color='green' success>
            Join the Webinar
          </Button>
          {renderUnregister(true)}
        </span>
      )
    }

    return (
      <span className={css.actions}>
        <p className={css.joinNote}>
          {
            windowOpen
              // Window's open but Zoom hasn't returned a join link yet (sync
              // creation failed and the retry worker hasn't caught up) —
              // say so rather than the untrue "opens in 15 minutes".
              ? 'Still preparing your join link — check back shortly.'
              : 'Join opens 15 minutes before start.'
          }
        </p>
        <Button {...commonProps} disabled color='green'>
          Join the Webinar
        </Button>
        {renderUnregister(true)}
      </span>
    )
  }

  if (isRegistered && isExternal) {
    return (
      <span className={css.actions}>
        <Button {...commonProps} onClick={() => openInNewTab(externalUrl)} color='green' success>
          Go to Event
        </Button>
        {renderUnregister(true)}
      </span>
    )
  }

  if (isRegistered) {
    return renderUnregister(false)
  }

  if (zoomEnabled && windowOpen) {
    return (
      <Button {...commonProps} disabled={isLoading} onClick={handleRegisterAndJoin}>
        Register and Join
      </Button>
    )
  }

  return (
    <Button
      {...commonProps}
      disabled={isLoading}
      onClick={register} // eslint-disable-line react/jsx-handler-names
    >
      Register
    </Button>
  )
}

RegisterButton.propTypes = {
  isRegistered: React.PropTypes.bool.isRequired,
  isLoading: React.PropTypes.bool.isRequired,
  register: React.PropTypes.func.isRequired,
  unregister: React.PropTypes.func.isRequired,
  isExternal: React.PropTypes.bool,
  externalUrl: React.PropTypes.string,
  zoomEnabled: React.PropTypes.bool,
  windowOpen: React.PropTypes.bool,
  joinUrl: React.PropTypes.string
}

export default RegisterButton
